import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { Toaster } from 'sonner';
import { fetchAuthSession } from 'aws-amplify/auth';
import './lib/i18n';
import { useTranslation } from 'react-i18next';
import Logo from './components/Logo';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import UserDetail from './pages/UserDetail';
import Profile from './pages/Profile';
import Applications from './pages/Applications';
import Attributes from './pages/Attributes';
import Matrix from './pages/Matrix';
import Groups from './pages/Groups';
import { useAuthStore } from './store/authStore';
import { useTheme } from './hooks/useTheme';

function AuthenticatedApp() {
  const { user } = useAuthenticator((context) => [context.user]);
  const { setUser } = useAuthStore();

  useEffect(() => {
    if (!user) return;
    fetchAuthSession()
      .then(() => {
        setUser({
          id: user.userId ?? '',
          email: user.signInDetails?.loginId ?? '',
          name: user.signInDetails?.loginId?.split('@')[0] ?? 'Usuario',
        });
      })
      .catch(() => {});
  }, [user, setUser]);

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/users" element={<Users />} />
        <Route path="/users/:username" element={<UserDetail />} />
        <Route path="/groups" element={<Groups />} />
        <Route path="/apps" element={<Applications />} />
        <Route path="/attributes" element={<Attributes />} />
        <Route path="/matrix" element={<Matrix />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

function AuthWrapper() {
  const { t } = useTranslation();

  return (
    <Authenticator
      hideSignUp={true}
      components={{
        Header() {
          return (
            <div className="flex flex-col items-center pb-6">
              <Logo className="h-10" tone="white" />
            </div>
          );
        },
        SignIn: {
          Header() {
            return (
              <div className="px-6 pt-6 pb-2 text-center">
                <h1 className="text-xl font-semibold text-foreground">
                  {t('auth.title')}
                </h1>
                <p className="mt-1 text-xs text-muted-foreground">
                  {t('auth.subtitle')}
                </p>
              </div>
            );
          },
        },
      }}
    >
      <AuthenticatedApp />
    </Authenticator>
  );
}

export default function App() {
  const { isDark } = useTheme();
  return (
    <>
      <AuthWrapper />
      <Toaster
        position="top-right"
        richColors
        closeButton
        theme={isDark ? 'dark' : 'light'}
      />
    </>
  );
}
